import { Icon } from '@iconify/react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Tooltip,
} from '@nextui-org/react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

export type AnalyticsType =
  | 'user-engagement'
  | 'agent-analytics'
  | 'system-access-metrics';

interface AnalyticsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (filters: AnalyticsFilters) => void;
  isLoading: boolean;
  type: AnalyticsType;
}

interface AnalyticsFilters {
  start_date?: string;
  end_date?: string;
}

const ANALYTICS_CONFIG: Record<
  AnalyticsType,
  {
    title: string;
    description: string;
    requiresDates: boolean;
    maxDateRangeDays?: number;
  }
> = {
  'user-engagement': {
    title: 'User Engagement Analytics',
    description:
      'Generate comprehensive user engagement report based on practice creation dates',
    requiresDates: true,
    maxDateRangeDays: 90, // 3 months
  },
  'agent-analytics': {
    title: 'Agent Analytics',
    description: 'Generate agent usage and performance metrics report',
    requiresDates: false,
  },
  'system-access-metrics': {
    title: 'System Access Metrics',
    description:
      'Generate system-wide access metrics including participants and active users',
    requiresDates: false,
  },
};

// Helper function to format date to YYYY-MM-DD
const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get default dates (last 30 days)
const getDefaultDates = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);

  return {
    start_date: formatDate(start),
    end_date: formatDate(end),
  };
};

export default function AnalyticsModal({
  isOpen,
  onClose,
  onGenerate,
  isLoading,
  type,
}: AnalyticsModalProps) {
  const config = ANALYTICS_CONFIG[type];
  const defaultDates = getDefaultDates();

  const [filters, setFilters] = useState<AnalyticsFilters>({
    start_date: defaultDates.start_date,
    end_date: defaultDates.end_date,
  });

  // Reset dates when modal opens
  useEffect(() => {
    if (isOpen) {
      const defaultDates = getDefaultDates();
      setFilters({
        start_date: defaultDates.start_date,
        end_date: defaultDates.end_date,
      });
    }
  }, [isOpen]);

  const validateDates = (): boolean => {
    if (config.requiresDates && (!filters.start_date || !filters.end_date)) {
      toast.error('Both start and end dates are required');
      return false;
    }

    if (filters.start_date && filters.end_date) {
      const start = new Date(filters.start_date);
      const end = new Date(filters.end_date);

      if (end < start) {
        toast.error('End date cannot be before start date');
        return false;
      }

      // Check if date range exceeds maximum allowed days
      if (config.maxDateRangeDays) {
        const daysDiff = Math.ceil(
          (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
        );
        if (daysDiff > config.maxDateRangeDays) {
          toast.error(
            `Date range cannot exceed ${config.maxDateRangeDays} days (${Math.floor(config.maxDateRangeDays / 30)} months)`
          );
          return false;
        }
      }
    }

    return true;
  };

  const handleGenerate = () => {
    if (!validateDates()) return;

    // Remove undefined or empty string values
    const cleanedFilters = Object.entries(filters).reduce(
      (acc, [key, value]) => {
        if (value !== undefined && value !== '') {
          acc[key as keyof AnalyticsFilters] = value;
        }
        return acc;
      },
      {} as AnalyticsFilters
    );

    onGenerate(cleanedFilters);
  };

  const handleDateChange = (
    field: 'start_date' | 'end_date',
    value: string
  ) => {
    // Ensure the date is in YYYY-MM-DD format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(value)) return;

    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleResetDates = () => {
    const defaultDates = getDefaultDates();
    setFilters({
      start_date: defaultDates.start_date,
      end_date: defaultDates.end_date,
    });
    toast.success('Dates reset to last 30 days');
  };

  const handleClearDates = () => {
    setFilters({
      start_date: undefined,
      end_date: undefined,
    });
    toast.success('Dates cleared - will include all historical data');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="center"
      classNames={{
        base: 'max-h-[90vh] overflow-y-auto',
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          {config.title}
        </ModalHeader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-default-500">{config.description}</p>

            <div className="bg-primary-50 dark:bg-primary-900/20 p-3 rounded-lg">
              <p className="text-sm text-primary-700 dark:text-primary-300">
                📧 The report will be generated asynchronously and sent to your
                email address when ready.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  Date Range {!config.requiresDates && '(Optional)'}
                </span>
                <div className="flex gap-1">
                  {!config.requiresDates && (
                    <Tooltip content="Clear dates to include all historical data">
                      <Button
                        size="sm"
                        variant="light"
                        onPress={handleClearDates}
                        disabled={isLoading}
                        startContent={<Icon icon="mdi:filter-off" width="16" />}
                      >
                        Clear
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip content="Reset to last 30 days">
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      onPress={handleResetDates}
                      disabled={isLoading}
                    >
                      <Icon icon="mdi:refresh" width="20" />
                    </Button>
                  </Tooltip>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  type="date"
                  label="Start Date"
                  placeholder="YYYY-MM-DD"
                  value={filters.start_date || ''}
                  onChange={(e) =>
                    handleDateChange('start_date', e.target.value)
                  }
                  pattern="\d{4}-\d{2}-\d{2}"
                  max={filters.end_date}
                />
                <Input
                  type="date"
                  label="End Date"
                  placeholder="YYYY-MM-DD"
                  value={filters.end_date || ''}
                  onChange={(e) => handleDateChange('end_date', e.target.value)}
                  pattern="\d{4}-\d{2}-\d{2}"
                  min={filters.start_date}
                />
              </div>
              {config.maxDateRangeDays && (
                <p className="text-xs text-warning">
                  Maximum date range: {config.maxDateRangeDays} days (
                  {Math.floor(config.maxDateRangeDays / 30)} months)
                </p>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="justify-between border-t mt-4">
          <Button variant="bordered" onPress={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleGenerate}
            isLoading={isLoading}
            startContent={
              !isLoading && <Icon icon="mdi:email-arrow-right" width="20" />
            }
          >
            Generate Report
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
